import { AUDIT_ACTION_GROUP_KEYS, AUDIT_ACTOR_TYPES, type AuditFilterOptions } from '@fos/shared/contracts/audit';
import { VIAS, type Via } from '@fos/shared/enums';
import { AUDIT_ACTION_GROUP_LABELS, AUDIT_ACTOR_TYPE_LABELS, VIA_LABELS } from '@fos/shared/labels';
import { SlidersHorizontal, X } from 'lucide-react';
import { useState } from 'react';
import { cn } from '../../../../lib/cn';
import { Avatar } from '../../../../ui/Avatar';
import { Button } from '../../../../ui/Button';
import { Combobox, type ComboboxOption } from '../../../../ui/Combobox';
import { DateInput } from '../../../../ui/DateTimeInput';
import { Field } from '../../../../ui/Field';
import { Select, type SelectOption } from '../../../../ui/Select';
import { ClientDot } from '../../../clients';
import { TOUCH_BUTTON } from '../../../mobile';
import { type AuditFilterState, activeFilterCount, EMPTY_FILTERS } from '../audit-ui';

const ALL = '__all';

/**
 * Pasek filtrów dziennika: kto (osoba albo AI / MCP / automat / system), rodzaj i akcja, obiekt, klient, kanał,
 * zakres dat. Desktop: siatka zawsze widoczna; telefon: zwinięta pod przyciskiem „Filtry (n)”.
 */
export function AuditFilters({
  value,
  onChange,
  options,
}: {
  value: AuditFilterState;
  onChange: (next: AuditFilterState) => void;
  options: AuditFilterOptions | undefined;
}) {
  const [open, setOpen] = useState(false);
  const count = activeFilterCount(value);
  const set = (patch: Partial<AuditFilterState>) => onChange({ ...value, ...patch });

  const whoOptions: ComboboxOption[] = [
    { value: ALL, label: 'Wszyscy' },
    ...(options?.people ?? []).map((p) => ({
      value: `user:${p.userId}`,
      label: p.name,
      description: p.active ? undefined : 'dostęp wyłączony',
      leading: <Avatar initials={p.initials} color={p.avatarColor} />,
    })),
    ...AUDIT_ACTOR_TYPES.filter((t) => t !== 'user').map((t) => ({
      value: `type:${t}`,
      label: AUDIT_ACTOR_TYPE_LABELS[t],
      description: 'rodzaj aktora',
    })),
  ];
  const groupOptions: SelectOption[] = [
    { value: ALL, label: 'Wszystkie' },
    ...AUDIT_ACTION_GROUP_KEYS.map((g) => ({ value: g, label: AUDIT_ACTION_GROUP_LABELS[g] })),
  ];
  const actionOptions: ComboboxOption[] = [
    { value: ALL, label: 'Wszystkie akcje' },
    ...(options?.actions ?? [])
      .filter((a) => !value.group || a.group === value.group)
      .map((a) => ({ value: a.action, label: a.label, description: a.action, keywords: [a.action] })),
  ];
  const objectOptions: SelectOption[] = [
    { value: ALL, label: 'Wszystkie' },
    ...(options?.objectTypes ?? []).map((t) => ({ value: t.type, label: t.label })),
  ];
  const clientOptions: ComboboxOption[] = [
    { value: ALL, label: 'Wszyscy klienci' },
    ...(options?.clients ?? []).map((c) => ({
      value: c.id,
      label: c.name,
      leading: <ClientDot color={c.color} />,
    })),
  ];
  const viaOptions: SelectOption[] = [
    { value: ALL, label: 'Wszystkie' },
    ...VIAS.map((v) => ({ value: v, label: VIA_LABELS[v] })),
  ];

  return (
    <div>
      <div className="flex items-center gap-2 md:hidden">
        <Button
          variant="secondary"
          size="s"
          className={TOUCH_BUTTON}
          icon={SlidersHorizontal}
          aria-expanded={open}
          aria-controls="audit-filters"
          onClick={() => setOpen((v) => !v)}
        >
          {count > 0 ? `Filtry (${count})` : 'Filtry'}
        </Button>
        {count > 0 && (
          <Button variant="ghost" size="s" className={TOUCH_BUTTON} icon={X} onClick={() => onChange(EMPTY_FILTERS)}>
            Wyczyść
          </Button>
        )}
      </div>
      <fieldset
        id="audit-filters"
        aria-label="Filtry dziennika"
        className={cn(
          'mt-3 grid-cols-2 gap-x-2 gap-y-3 md:mt-0 md:grid md:grid-cols-4 md:gap-3',
          open ? 'grid' : 'hidden',
        )}
      >
        <Field label="Kto">
          <Combobox
            value={value.who ?? ALL}
            onValueChange={(v) => set({ who: v === ALL ? null : v })}
            options={whoOptions}
            searchPlaceholder="Szukaj osoby…"
            size="s"
          />
        </Field>
        <Field label="Rodzaj">
          <Select
            value={value.group ?? ALL}
            onValueChange={(v) => set({ group: v === ALL ? null : (v as AuditFilterState['group']), action: null })}
            options={groupOptions}
            size="s"
          />
        </Field>
        <Field label="Akcja">
          <Combobox
            value={value.action ?? ALL}
            onValueChange={(v) => set({ action: v === ALL ? null : v })}
            options={actionOptions}
            searchPlaceholder="Szukaj akcji…"
            size="s"
          />
        </Field>
        <Field label="Obiekt">
          <Select
            value={value.objectType ?? ALL}
            onValueChange={(v) => set({ objectType: v === ALL ? null : v })}
            options={objectOptions}
            size="s"
          />
        </Field>
        <Field label="Klient">
          <Combobox
            value={value.clientId ?? ALL}
            onValueChange={(v) => set({ clientId: v === ALL ? null : v })}
            options={clientOptions}
            searchPlaceholder="Szukaj klienta…"
            size="s"
          />
        </Field>
        <Field label="Kanał">
          <Select
            value={value.via ?? ALL}
            onValueChange={(v) => set({ via: v === ALL ? null : (v as Via) })}
            options={viaOptions}
            size="s"
          />
        </Field>
        <Field label="Od">
          <DateInput value={value.from ?? ''} onValueChange={(v) => set({ from: v || null })} size="s" />
        </Field>
        <Field label="Do">
          <DateInput value={value.to ?? ''} onValueChange={(v) => set({ to: v || null })} size="s" />
        </Field>
      </fieldset>
      {count > 0 && (
        <div className="mt-2 hidden md:block">
          <Button variant="ghost" size="s" className={TOUCH_BUTTON} icon={X} onClick={() => onChange(EMPTY_FILTERS)}>
            Wyczyść filtry
          </Button>
        </div>
      )}
    </div>
  );
}
